/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getSites} from './site-utils'
import {urlPartPositions} from '../constants'
import {encodeUrlLocale} from './locale'

/**
 * Construct literal routes based on url config
 *      with site and locale references (ids and aliases) from each in your application config
 *
 * @param {array} routes - array of routes to be reconstructed
 * @param {object} config
 * @param {object} options - options if there are any
 * @param {array} options.ignoredRoutes - routes that does not need be reconstructed
 * @return {array} - list of routes objects that has site and locale refs, prefixed ones keep the
 *      original path in `pathPattern`.
 */
export const configureRoutes = (routes = [], config, {ignoredRoutes = [], site, locale}) => {
    if (!routes.length) return []
    if (!config) return routes

    const {url: urlConfig} = config.app

    const allSites = site ? [site] : getSites()
    if (!allSites) return routes

    // prepare a map of site id or alias to locales
    const siteLocaleMap = new Map(
        allSites.flatMap((site) => {
            // append site ids and aliases to an array
            const siteRefs = [site.alias, site.id].filter(Boolean)
            let localeRefs = []
            // append locale ids and aliases to an array
            const allLocales = locale ? [locale] : site.l10n.supportedLocales
            allLocales.forEach((locale) => {
                localeRefs.push(locale.alias)
                localeRefs.push(locale.id)
            })
            localeRefs = localeRefs.filter(Boolean).map(encodeUrlLocale)

            return siteRefs.map((siteId) => [siteId, localeRefs])
        })
    )

    const {locale: localePosition, site: sitePosition} = urlConfig

    // use a map to store the routes, removing duplicates while keeping the order
    const outputRoutesMap = new Map()
    const addRoute = (route) => {
        outputRoutesMap.set(route.path, route)
    }
    for (let i = 0; i < routes.length; i++) {
        const {path, ...others} = routes[i]
        const rest = {...others, pathPattern: path}

        if (ignoredRoutes.includes(path)) {
            addRoute(routes[i])
        } else {
            siteLocaleMap.forEach((localeRefs, site) => {
                if (
                    localePosition === urlPartPositions.PATH &&
                    sitePosition === urlPartPositions.PATH
                ) {
                    // append the route that only has site
                    addRoute({
                        path: `/${site}${path}`,
                        ...rest
                    })
                    localeRefs.forEach((locale) => {
                        // append the route that has both site and locale
                        addRoute({
                            path: `/${site}/${locale}${path}`,
                            ...rest
                        })
                        // append the route that only has locale
                        addRoute({
                            path: `/${locale}${path}`,
                            ...rest
                        })
                    })
                }

                if (
                    localePosition !== urlPartPositions.PATH &&
                    sitePosition === urlPartPositions.PATH
                ) {
                    // construct the routes that only has site id or alias
                    addRoute({
                        path: `/${site}${path}`,
                        ...rest
                    })
                }
                if (
                    localePosition === urlPartPositions.PATH &&
                    sitePosition !== urlPartPositions.PATH
                ) {
                    // construct the routes that only has locale id or alias
                    localeRefs.forEach((locale) => {
                        addRoute({
                            path: `/${locale}${path}`,
                            ...rest
                        })
                    })
                }
            })
            // origin route will be at the bottom
            addRoute(routes[i])
        }
    }
    return [...outputRoutesMap.values()]
}
